import { format, parseISO } from 'date-fns';
import { createRoot } from 'react-dom/client';
import {
  MEAT_TAGS,
  SHELLFISH_TAGS,
  SOY_TAGS,
  SEAFOOD_TAGS,
  ACTIVE_PROTEIN_TAGS,
  SPICY_TAGS_MAP,
} from '../constants/constants';

const isMobile = window.matchMedia(
  'only screen and (max-device-width: 800px)'
).matches;

// this allow us to render the app only id the dom element exists on the page.
export function renderApp(component, id) {
  let domElement = document.getElementById(id);
  if (!domElement) return;
  createRoot(domElement).render(component);
}

export function getCurrentPathName() {
  let pathname = window.location.pathname;
  return pathname[pathname.length - 1] !== '/' ? `${pathname}/` : pathname;
}

export class BodyScroll {
  constructor() {
    this.scrollY = null;
  }

  stop() {
    if (window.pageHasNoScroll) return;
    this.scrollY = window.scrollY;
    if (isMobile) {
      // to fix the weird stretching when a modal is shown
      document.body.style.position = 'fixed';
      document.body.style.maxWidth = '100%';
    } else {
      document.body.style.overflow = 'hidden';
    }
  }

  activate() {
    if (window.pageHasNoScroll) return;
    document.body.style.position = 'initial';
    document.body.style.overflow = 'initial';
    if (isMobile) {
      window.scrollTo(0, this.scrollY);
    }
  }
}

export const getPairingDataImg = (pd, src) => {
  let cssValue = src === 'src' ? pd.featured_img : `url(${pd.featured_img})`;
  return pd.featured_img ? cssValue : null;
};

export const deepCloneObj = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};

// from query params return an object of keys and values
// pass a modifier function to modify the values if necessary.
export const parseQP = (modifier = (v) => v) => {
  let qString = window.location.search;
  // remove the first question mark
  qString = qString.replace('?', '');
  return qString.split('&').reduce((obj, nextStr) => {
    if (nextStr.includes('=')) {
      const [field, value] = nextStr.split('=');
      const modifiedValue = modifier(value, field);
      if (modifiedValue !== undefined && modifiedValue !== null) {
        obj[field] = modifiedValue;
      }
    }
    return obj;
  }, {});
};

export const commaNumber = (n) =>
  n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

// paramenter should be a word or 2 words separated by a symbol
export const capitalize = (s) => {
  let match = s.match(/\W/);
  let symbol = match ? s.match(/\W/)[0] : ' ';
  let arrOfWords = s.split(symbol);
  let capitalizedWords = arrOfWords.map((word) => {
    return word[0].toUpperCase() + word.slice(1).toLowerCase();
  });
  return capitalizedWords.join(symbol);
};

// This creates component Selector options object from an array of objects
// example: {value: [primitive], optionText[string]: , img: [string]}
/**
 *
 * @param { array of objects to make into selector options } arr
 * @param {{ uppercase: type bool, uppercase: type bool }} options
 * @returns
 */
export const createSelectorOptions = (
  arr,
  valueKey,
  optionTextKey,
  imgKey,
  subtext,
  options = {}
) => {
  if (!Array.isArray(arr)) {
    arr = Object.values(arr);
  }
  return arr.reduce((acc, nextOption) => {
    acc[nextOption[valueKey]] = {
      value: nextOption[valueKey],
      optionText:
        nextOption[optionTextKey] && options.uppercase
          ? nextOption[optionTextKey].toUpperCase()
          : nextOption[optionTextKey],
      img: nextOption[imgKey],
      subtext: nextOption[subtext],
    };
    if (options.optionHTML) {
      acc[nextOption[valueKey]]['optionHTML'] =
        nextOption[optionTextKey] && options.uppercase
          ? nextOption[optionTextKey].toUpperCase()
          : nextOption[optionTextKey];
    }
    return acc;
  }, {});
};

// For use on Edit Delivery page
export const hasAppliedFilters = (appliedFilters) =>
  appliedFilters &&
  Object.values(appliedFilters).filter((ele) => {
    if (ele !== null) {
      return Array.isArray(ele) ? ele.length > 0 : true;
    } else {
      return false;
    }
  }).length >= 1;

export const sendPageView = () => {
  if (window.ga) {
    window.ga('send', 'pageview', getCurrentPathName());
  }
};

//scroll cards container to the top used in edit-delivery
export const scrollItemsContainerToTop = () => {
  const el = document.getElementsByClassName('items-display-container')[0];
  if (el) {
    el.scroll(0, 0);
  }
};

// used in edit-delivery
export const scrollSideBar = () => {
  setTimeout(() => {
    for (let element of document.getElementsByClassName('sidebar-content')) {
      element.scroll(0, 0);
    }
  }, 350);
};

// strips non alphanumeric chars from a string
export const stripNonAlpha = (str) => {
  return str.replace(/^\W+|\W+$/g, '');
};

export const waitFor = (ms) => new Promise((r) => setTimeout(r, ms));

export const runAfterIdle = (func, seconds) => {
  let pointer = 0;
  document.onmousemove = () => (pointer = 0);
  document.onkeydown = () => (pointer = 0);
  document.ontouchstart = () => (pointer = 0);
  const interval = setInterval(() => {
    pointer += 1;
    if (pointer >= seconds) {
      func();
      clearInterval(interval);
    }
  }, 1000);
};

export const scrollSpeedCalculator = () => {
  const delay = 50;
  let lastPosition, newPosition, timer, delta;
  function clear() {
    lastPosition = null;
    delta = 0;
  }

  clear();
  return function () {
    newPosition = window.scrollY;
    if (lastPosition != null && newPosition < lastPosition) {
      delta = lastPosition - newPosition;
    }
    lastPosition = newPosition;
    clearTimeout(timer);
    timer = setTimeout(clear, delay);
    return delta;
  };
};

export const formatDate = (date) => {
  return format(parseISO(date), 'EEEE, MMMM do');
};

/* swipeActions is an js object that contains directions and actions as key -> values
  {
    right: [function],
    left: [function],
    up: [function],
    down: [function],
  }
*/
export const onSwipe = (element, swipeActions) => {
  let xDown = null;
  let yDown = null;

  const getTouches = (evt) => {
    return evt.touches || evt.originalEvent.touches; // browser API , jQuery
  };

  const handleTouchStart = (evt) => {
    const firstTouch = getTouches(evt)[0];
    xDown = firstTouch.clientX;
    yDown = firstTouch.clientY;
  };

  const handleTouchMove = (evt) => {
    if (!xDown || !yDown) {
      return;
    }

    let xUp = evt.touches[0].clientX;
    let yUp = evt.touches[0].clientY;

    let xDiff = xDown - xUp;
    let yDiff = yDown - yUp;

    if (Math.abs(xDiff) > Math.abs(yDiff)) {
      if (xDiff > 0) {
        /* right swipe */
        swipeActions.right && swipeActions.right();
      } else {
        /* left swipe */
        swipeActions.left && swipeActions.left();
      }
    } else {
      if (yDiff > 0) {
        /* down swipe */
        swipeActions.down && swipeActions.down();
      } else {
        /* up swipe */
        swipeActions.up && swipeActions.up();
      }
    }
    /* reset values */
    xDown = null;
    yDown = null;
  };

  element.addEventListener('touchstart', handleTouchStart, false);
  element.addEventListener('touchmove', handleTouchMove, false);
};

// creates a function that filters the tags depending on the customer's deitary preferences:
export const createTagDietFilterFunc = (dietPrefs) => (tags) => {
  // only return the active protein tags
  return tags.filter((t) => {
    if (dietPrefs.is_vegan || dietPrefs.is_vegetarian) {
      if (MEAT_TAGS.includes(t.id) || SEAFOOD_TAGS.includes(t.id)) {
        return false;
      }
    }
    if (dietPrefs.is_pescatarian) {
      if (MEAT_TAGS.includes(t.id)) {
        return false;
      }
    }
    if (dietPrefs.is_shellfish_free) {
      if (SHELLFISH_TAGS.includes(t.id)) {
        return false;
      }
    }
    if (dietPrefs.is_soy_free) {
      if (SOY_TAGS.includes(t.id)) {
        return false;
      }
    }
    return true;
  });
};

export const copySharableLink = (event) => {
  const linkField = event.currentTarget.querySelector('.share-link');
  linkField.select();
  // selection = linkField.value
  document.execCommand('copy');
  const toaster = new Toaster();
  toaster.toast('link_copy', 'Copied to clipboard');
};

/**
 *
 * @param {Array of tags} tagsArr
 * @returns {
 *  spicyLevel[int]: {
 *    name: t.name,
      remark: t.remark,
      thumbnail: t.thumbnail_url,
 *  }
    ... (for all tags)
 * }
 */
export const spicyTagsBySpicyLevel = (tagsArr) => {
  const spicyTagsDataFromArr = tagsArr.reduce((acc, t) => {
    let spicyLevel = SPICY_TAGS_MAP[t.id];
    acc[spicyLevel] = {
      name: t.name,
      remark: t.remark,
      thumbnail: t.thumbnail_url,
    };
    return acc;
  }, {});
  return spicyTagsDataFromArr;
};

export const makeSegmentProductList = (mappingObj, category) => {
  let store = [];
  for (const [id, obj] of Object.entries(mappingObj)) {
    store.push({
      product_id: id,
      category: category,
      count: obj.qty ? obj.qty : obj.count,
    });
  }
  return store;
};
